import { Message, User, Attachment, JumpResult, ChannelAttachmentsResult, NotificationPrivacy, UserProfileInfo } from '../types';
import { apiFetch, API_BASE_URL, dispatchUnauthorized } from './apiClient';

/**
 * Electron 애플리케이션 서비스 레이어
 */

export const dbService = {
  /**
   * 특정 채널의 메시지를 조회합니다.
   * before: 그 메시지보다 과거 방향 / after: 최신 방향 (날짜 점프 후 아래로 이어보기)
   */
  getMessages: async (
    channelId: number,
    limit = 100,
    before?: number,
    after?: number,
    // 채널을 빠르게 훑을 때 이전 채널 요청을 끊기 위한 신호. 넘기지 않으면 기존과 동일하게 동작한다.
    signal?: AbortSignal,
  ): Promise<Message[]> => {
    const beforeQuery = before ? `&before=${before}` : '';
    const afterQuery = after ? `&after=${after}` : '';
    return apiFetch<Message[]>(`/channels/${channelId}/messages?limit=${limit}${beforeQuery}${afterQuery}`, { signal });
  },

  /**
   * 날짜 점프 — 지정 시각 이후 첫 메시지를 앵커로 그 주변 메시지를 조회합니다.
   * fromIso: 로컬 자정 기준 ISO 문자열
   */
  jumpToDate: async (channelId: number, fromIso: string): Promise<JumpResult> => {
    return apiFetch<JumpResult>(`/channels/${channelId}/messages/jump?from=${encodeURIComponent(fromIso)}`);
  },

  /**
   * 메시지 점프 — 특정 메시지를 앵커로 그 주변 메시지를 조회합니다 (공지·서랍 원문 이동).
   * 삭제됐거나 접근 불가한 메시지면 anchorId: null
   */
  jumpToMessage: async (channelId: number, messageId: number): Promise<JumpResult> => {
    return apiFetch<JumpResult>(`/channels/${channelId}/messages/jump?messageId=${messageId}`);
  },

  /**
   * 채널 첨부 서랍 — 사진/파일 목록 (최신순, attachment id 커서 페이징)
   */
  getChannelAttachments: async (
    channelId: number,
    type: 'image' | 'file',
    before?: number
  ): Promise<ChannelAttachmentsResult> => {
    const beforeQuery = before ? `&before=${before}` : '';
    return apiFetch<ChannelAttachmentsResult>(`/channels/${channelId}/attachments?type=${type}${beforeQuery}`);
  },

  /**
   * 메시지 삭제 (Soft Delete)
   */
  deleteMessage: async (messageId: number): Promise<void> => {
    await apiFetch<void>(`/messages/${messageId}`, { method: 'DELETE' });
  },

  /**
   * 메시지 전달 (Forward) — 원본 상한 20개 / 대상 채널 상한 10개, 대상별 부분 성공
   */
  forwardMessages: async (
    messageIds: number[],
    targetChannelIds: number[]
  ): Promise<{ results: Array<{ channelId: number; ok: boolean; error?: string; messageCount?: number }> }> => {
    return apiFetch(`/messages/forward`, {
      method: 'POST',
      body: JSON.stringify({ messageIds, targetChannelIds }),
    });
  },

  /**
   * 조직도(전체 유저) 데이터 가져오기
   */
  getOrganization: async (): Promise<User[]> => {
    return apiFetch<User[]>('/organization');
  },

  /**
   * 특정 계열사의 직원 검색
   */
  searchUsersByAffiliate: async (
    affiliateId: string,
    query?: string,
    options?: { includeUnapproved?: boolean }
  ): Promise<User[]> => {
    const params = new URLSearchParams({ affiliateId });
    if (query && query.trim()) {
      params.append('q', query.trim());
    }
    // 승인 필터 옵트아웃 — 관리자 콘솔 '로그인 승인' 화면 전용.
    // 서버가 isAdmin을 재검사하므로(403) 이 플래그만으로 권한이 생기지는 않는다.
    if (options?.includeUnapproved) {
      params.append('includeUnapproved', '1');
    }
    return apiFetch<User[]>(`/users/search?${params.toString()}`);
  },

  /**
   * 상대방 프로필 (프로필 모달) — 이름·소속·메일 주소·아바타. 사번으로 조회하며 계열사는 서버가 찾는다.
   */
  getUserProfile: async (userId: string | number): Promise<UserProfileInfo> => {
    return apiFetch<UserProfileInfo>(`/users/${encodeURIComponent(String(userId))}/profile`);
  },

  /**
   * 파일 업로드
   *
   * NOTE: XHR을 사용하므로 apiFetch가 아닌 직접 401 처리.
   */
  uploadFiles: async (files: File[], onProgress?: (percent: number) => void): Promise<Attachment[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const token = localStorage.getItem('authToken');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          dispatchUnauthorized();
          reject(new Error('세션이 만료되었습니다. 다시 로그인해주세요.'));
          return;
        }
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data.files);
          } else {
            reject(new Error(data.error || '업로드 실패'));
          }
        } catch {
          reject(new Error('업로드 응답 파싱 실패'));
        }
      };

      xhr.onerror = () => {
        reject(new Error('업로드 중 네트워크 오류가 발생했습니다.'));
      };

      xhr.open('POST', `${API_BASE_URL}/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  },

  /**
   * 아바타 URL 서버에 저장 — 프리셋·외부 URL 전용이다.
   * 업로드한 사진 경로(`/uploads/avatars/...`)는 서버가 400으로 거부한다(uploadAvatar가 담당).
   */
  updateAvatar: async (avatarUrl: string): Promise<void> => {
    await apiFetch<void>('/profile/avatar', {
      method: 'PUT',
      body: JSON.stringify({ avatarUrl }),
    });
  },

  /**
   * 알림에 표시할 내용 수준 저장 — 기기별이 아니라 계정 전체 설정이다.
   * 서버가 저장 직후 그 계정의 전 세션에 user:notify-privacy-changed를 보내므로,
   * 호출부는 로컬 반영만 하고 다른 기기 동기화는 신경 쓰지 않아도 된다.
   * 실패는 throw — 호출부가 토스트를 띄우고 선택을 되돌린다.
   */
  updateNotificationPrivacy: async (privacy: NotificationPrivacy): Promise<void> => {
    await apiFetch<void>('/profile/notification-privacy', {
      method: 'PUT',
      body: JSON.stringify({ privacy }),
    });
  },

  /**
   * 내 접속 상태 수동 선택 — 'auto'는 자동 판정(유휴·잠금) 복귀다.
   * 접속 중에만 유지되는 값이라 서버도 DB에 쓰지 않는다. 저장 직후 서버가
   * 전 접속자에게 상태 점을, 내 전 세션에 user:presence-manual을 보내므로
   * 호출부는 로컬 반영만 한다. 실패는 throw — 호출부가 토스트를 띄우고 선택을 되돌린다.
   */
  updatePresenceStatus: async (status: 'auto' | 'away' | 'busy'): Promise<void> => {
    await apiFetch<void>('/profile/presence', {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * 아바타 사진 업로드 — 크롭·필터를 마친 이미지를 올리고 저장된 URL을 돌려받는다.
   * 서버가 user_profiles까지 갱신하고 user:avatar-changed를 브로드캐스트한다.
   * 그래서 이 호출 뒤에 updateAvatar(PUT)를 부르면 안 된다 — UPSERT와 전역 브로드캐스트가
   * 2배가 되고, 두 요청의 순서가 보장되지 않아 사진을 연달아 바꿀 때 DB가 이미 지워진 파일을
   * 가리킬 수 있다. App.handleAvatarChange의 { alreadySaved: true }가 그 계약을 강제한다.
   */
  uploadAvatar: async (blob: Blob): Promise<string> => {
    const formData = new FormData();
    // 파일명은 서버가 uuid로 다시 짓는다(확장자는 MIME으로 판정) — 표시용 이름일 뿐이다
    formData.append('avatar', blob, 'avatar.jpg');
    const data = await apiFetch<{ success: boolean; avatarUrl: string }>('/profile/avatar/image', {
      method: 'POST',
      body: formData,
    });
    return data.avatarUrl;
  },

  /**
   * 업로드한 파일 삭제 — 메시지·예약에 담기지 않은 파일만 서버가 지운다(참조 중이면 409).
   * 예약 모달이 업로드 직후 예약 등록에 실패했을 때 방금 올린 파일을 치우는 데 쓴다.
   */
  deleteFile: async (fileUrl: string): Promise<void> => {
    const match = fileUrl.match(/\/uploads\/([^/]+)\/(.+)$/);
    if (!match) {
      throw new Error('Invalid file URL');
    }

    const [, date, filename] = match;
    // 파일명에 #·?·%가 들어 있으면 그대로 붙일 때 경로가 잘린다
    await apiFetch<void>(`/upload/${encodeURIComponent(date)}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }
};
